<!--Kamera für ANDROID integrieren - Kundn beschwerden sich das man nicht direkt ein Foto machen kann!-->
<script lang="ts" setup>
import { ref, onMounted, reactive, onUnmounted } from "vue";
import {
  CommonEditControlEmits,
  EditControlProps,
  getDefaultEditorControlConfig,
  mounted,
  valueChanged,
} from "@/editor/EditComponentStatic";
import { showPDF2, showImage, openFile } from "@/PdfHelper";
import { createBase64Image, resizeImage, writeTempFileAndOpen } from "@/FileHelper";
import Constants from "../Constants";
import { Capacitor } from "@capacitor/core";
import { globalsBezeichnung } from "@/local";
import { Globals } from "@/Globals";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import AUApi from "@/api";
import { Buffer } from "buffer";
import { useRegisterValidation } from "@/components/EditorValidator";

declare interface FileValue {
  aic?: number;
  name?: string;
  data?: string;
  type?: string;
}

const props = defineProps<EditControlProps>();
const emitFn = defineEmits<CommonEditControlEmits>();

interface EditFileChoicerUiData extends EditorControlUiData {
  showCameraIcon?: boolean;
}

const { config, ui } = getDefaultEditorControlConfig<
  EditorControlConfig,
  EditControlProps,
  EditFileChoicerUiData
>(props, emitFn);
const uiData = reactive(ui);

config.validate = validate;
useRegisterValidation({ config });

uiData.showCameraIcon = Capacitor.getPlatform() === "android";

const imageName = ref("");
const placeholder = ref("Upload your documents");
let imageFile = ref<File>();
const loading = ref(false);
let base64data = ref("");
const pdf = ref(false);
const pdfFile = ref<FileValue>();

const mimeType = ref("image/jpg");
const pdfBlobUrl = ref("");

function createPdfBlobUrl(data: string) {
  if (pdfBlobUrl.value) URL.revokeObjectURL(pdfBlobUrl.value);
  pdfBlobUrl.value = "";
  if (!data) return;
  const base64 = data.startsWith("data:") ? data.split(",")[1] : data;
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  pdfBlobUrl.value = URL.createObjectURL(new Blob([arr], { type: "application/pdf" }));
}

onUnmounted(() => {
  if (pdfBlobUrl.value) URL.revokeObjectURL(pdfBlobUrl.value);
});

onMounted(() => {
  mounted(uiData, props, config);

  const x: FileValue = config.initValue;
  if (x?.data) {
    // bei pdf nicht laden!
    if (x.type !== "application/pdf") {
      base64data.value = x.data;
    } else {
      pdf.value = true;
      const image: any = x;
      pdfFile.value = image;
      createPdfBlobUrl(image.data);
    }
  } else if (x.type === "application/pdf") {
    pdf.value = true;
    const image: any = x;
    pdfFile.value = image;
    imageName.value = image.name;
    createPdfBlobUrl(image.data ?? "");
  }
  if (x?.name) {
    mimeType.value = x.type ?? "image/jpg";
    imageName.value = x.name;
    if (!base64data.value) {
      loadImage(x);
    }
  }
});
function loadImage(image: any) {
  if (pdf.value) {
    return;
  }
  if (image.datentyp === "Doku") {
    base64data.value = AUApi.Current.fullUrl(
      "pdf2/" + AUApi.Current.user.id + ":" + +image.aic + "_" + image.name
    );
  } else {
    base64data.value = AUApi.Current.fullUrl(
      "image2/" + AUApi.Current.user.id + ":" + +image.aic + "_" + image.name
    );
  }
}
function onShowPdf() {
  if (pdfFile.value?.name && pdfFile.value?.aic) {
    showPDF2(pdfFile.value!.name, "" + pdfFile.value!.aic, AUApi.Current.user.id);
  } else alert("nothing to show");
}
async function onShowImage(image: any) {
  if (image?.aic) showImage(image, image.aic, AUApi.Current.user.id, mimeType.value);
  else {
    writeTempFileAndOpen("preview.tmp", mimeType.value, base64data.value);
  }
}

async function fileChanged(files: File | File[]) {
  pdf.value = false;
  if (Array.isArray(files) && (files?.length ?? 0) > 1) alert("select only one file!");
  if (files) {
    var file: File = <File>files;
    imageName.value = file.name;
    if (imageName.value.lastIndexOf(".") <= 0) {
      return;
    }
    try {
      if (file.type !== "application/pdf") {
        let thumbnail;
        // console.log("Android: " + Constants.platformAndroid);
        if (Constants.isMobile && Capacitor.getPlatform() !== "android") {
          thumbnail = await resizeImage(file, 1100, 1100);
          // wenn IOS dann nicht so stark komprimieren!
        } else {
          thumbnail = await resizeImage(file, 1000, 1000);
        }
        // const thumbnail = await resizeImage(file, 1000, 1000);
        const base64 = await createBase64Image(thumbnail);
        // am Handy keine Vorschau! macht Probleme
        //if (!Constants.isMobile) {
        base64data.value = base64;
        //}
        // me.valueChanged({ data: base64, name: file.name + ".jpg" });
        valueChanged(config, uiData, { data: base64, name: file.name });
      } else {
        const base64 = await createBase64Image(file);
        //if (!Constants.isMobile) {
        base64data.value = base64;
        pdf.value = true;
        //}
        valueChanged(config, uiData, { data: base64, name: file.name });
        createPdfBlobUrl(base64);
      }
    } catch (error) {
      loading.value = false;
      console.log("error reading File:" + error);
      alert("Kann File leider nicht lesen!");
      imageFile.value = undefined;
    }
  }
}
async function getPhoto() {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      width: 1000,
      height: 1000,
      source: CameraSource.Camera,
      saveToGallery: false,
      promptLabelHeader: "Foto machen",
      correctOrientation: true,
      promptLabelCancel: "Abbrechen",
    });

    if (image && image.base64String) {
      loading.value = true;
      // const response = await fetch(image.base64String!);
      // const blob = await response.blob();
      const buffer = Buffer.from(image.base64String!, "base64");
      const file = new File([buffer], "photo.jpg", {
        type: "image/" + image.format,
      });
      //imageFile.value = file;
      fileChanged(file);
      loading.value = false;
    }
  } catch (error) {
    console.log("Camera error: " + error);
    loading.value = false;
  }
}
function validate() {
  if (uiData.required) {
    if (imageName.value) {
      uiData.errorText = "";
      return true;
    }
    uiData.errorText = config.requiredMessage;
    return false;
  }
  return true;
}
function clear() {
  imageName.value = "";
  base64data.value = "";
}
</script>
<template>
  <div class="wrap" v-if="uiData.showMutable">
    <v-tooltip
      :disabled="!uiData.showtooltip || !uiData.tooltip"
      location="bottom"
      max-width="300px"
      :text="uiData.tooltip"
    >
      <template v-slot:activator="{ props }">
        <div v-bind="props" v-if="!uiData.disabled">
          <v-file-input
            id="image"
            ref="image"
            :label="
              globalsBezeichnung(Globals.Begriff, 'Web_Datei_Auswahl', 'Datei Auswahl')
            "
            density="compact"
            variant="outlined"
            small-chips
            accept="image/*,.pdf"
            :placeholder="imageName || placeholder"
            :disabled="uiData.disabled"
            v-model="imageFile"
            :loading="loading"
            @update:modelValue="fileChanged"
            :show-size="true"
            type="file"
            truncate-length="15"
            clearable
            @click:clear="clear"
            :error-messages="uiData.errorText"
            color="warning"
            persistent-hint
            :append-icon="uiData.showCameraIcon ? 'mdi-camera' : ''"
            @click:append="getPhoto()"
          ></v-file-input>

          <div v-if="imageName && !pdf">{{ imageName }}</div>
          <v-btn
            v-if="pdf && pdfFile"
            @click="onShowPdf"
            density="comfortable"
            size="large"
            class="pdf-btn"
            ><span class="pdf-name"> {{ imageName }}</span
            ><v-icon>mdi-file-pdf-box</v-icon></v-btn
          >

          <v-img
            v-if="base64data && !pdf"
            :src="base64data"
            max-width="90%"
            :aspect-ratio="16 / 9"
            max-height="100%"
            @click="onShowImage(uiData.data)"
            contain
          />
          <iframe
            v-if="pdfBlobUrl && !Constants.isMobile"
            :src="pdfBlobUrl + '#toolbar=0&navpanes=0&view=FitH'"
            style="width: 90%; height: 500px; border: none"
            class="pt-2"
          />
          <ValueChangeTracker
            v-if="uiData.isChanged || props.disableValueChangeTracker"
          />
        </div>
        <v-row v-if="!imageName && uiData.disabled && uiData.description">
          <v-col cols="2" class="py-2 mb-2">
            <span style="white-space: nowrap">{{ uiData.description }}: </span>
          </v-col>
        </v-row>

        <div class="ma-0" v-if="imageName && uiData.disabled">
          <div v-if="imageName && !pdf">{{ imageName }}</div>
          <v-btn
            density="comfortable"
            size="large"
            v-if="pdf"
            class="pdf-btn"
            @click="onShowPdf()"
            variant="outlined"
            ><v-icon>mdi-file-pdf-box</v-icon
            ><span class="pdf-name">{{ imageName }}</span></v-btn
          >
          <v-img
            v-if="base64data"
            :src="base64data"
            max-width="90%"
            :aspect-ratio="16 / 9"
            @click="onShowImage(uiData.data)"
            contain
          />
          <iframe
            v-if="pdfBlobUrl && !Constants.isMobile"
            :src="pdfBlobUrl + '#toolbar=0&navpanes=0&view=FitH'"
            style="width: 90%; height: 500px; border: none"
            class="pt-2"
          />
        </div>
      </template>
    </v-tooltip>
  </div>
</template>

<style scoped>
.v-responsive__sizer {
  padding: 0px !important;
}
.pdf-btn {
  width: 100%;
  max-width: 100%;
  min-height: 44px;
  height: auto;
}
:deep(.pdf-btn .v-btn__content) {
  white-space: normal;
  text-align: left;
  justify-content: flex-start;
  width: 100%;
  gap: 6px;
}
.pdf-name {
  white-space: normal;
  text-align: left;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
  display: block;
  width: 100%;
  font-size: 12px;
  line-height: 1.4;
}
</style>
